import { useState, useEffect, useCallback } from 'react';

/**
 * Orders tool health — what the orders.re3gifts.com tool has logged as errors
 * recently, each with an ignore / watch / act verdict and what to do about
 * it, plus the live signals: last night's SFTP push to Capital One and how
 * fast the last 10 orders' QR art rendered.
 *
 * Replaces the Retool "MUSE Error Report (Last 48 Hours)" email, which listed
 * raw counts per type and could not tell noise from an outage. The alert
 * email (GET /api/tool-health/cron) uses the same rules and only fires when a
 * human needs to act.
 *
 * Fetches on mount, independently of the dashboard's Promise.all: the log
 * table has no usable index, so the summary is a ~2s sequential scan that
 * must not delay the first render.
 */
const WINDOWS = [
  { hours: 24, label: '24h' },
  { hours: 48, label: '48h' },
  { hours: 168, label: '7 days' },
];

const VERDICT_STYLE = {
  act: 'bg-red-100 text-red-700',
  watch: 'bg-amber-100 text-amber-700',
  ignore: 'bg-emerald-100 text-emerald-700',
};

const STATUS_DOT = {
  healthy: 'bg-emerald-500',
  watch: 'bg-amber-500',
  act: 'bg-red-500',
};

const LATENCY_STYLE = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  slow: 'bg-amber-50 text-amber-700 border-amber-200',
  rendering: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  stuck: 'bg-red-50 text-red-700 border-red-200',
  unrenderable_only: 'bg-gray-50 text-gray-500 border-gray-200',
  no_codes: 'bg-gray-50 text-gray-500 border-gray-200',
};

function fmtTime(iso) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtClock(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function latencyLabel(l) {
  switch (l.state) {
    case 'ok':
    case 'slow':
      return `${l.seconds}s`;
    case 'rendering':
      return `${l.done}/${l.codes} rendering`;
    case 'stuck':
      return `${l.done}/${l.codes} stuck`;
    case 'unrenderable_only':
      return `${l.done}/${l.codes} · # serial`;
    default:
      return 'no codes';
  }
}

function VerdictBadge({ verdict }) {
  return (
    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${VERDICT_STYLE[verdict] || VERDICT_STYLE.watch}`}>
      {verdict}
    </span>
  );
}

function headline(data) {
  if (!data) return '';
  const { act, watch } = data.counts;
  const extra = [];
  if (data.sftp.status === 'failed' || data.sftp.status === 'missing') extra.push('SFTP');
  if (data.renderLatency.some((l) => l.state === 'stuck')) extra.push('an order stuck on art');
  if (data.status === 'healthy') return 'Orders tool: healthy';
  const parts = [];
  if (act > 0) parts.push(`${act} to act on`);
  if (watch > 0) parts.push(`${watch} to watch`);
  if (extra.length) parts.push(...extra);
  return `Orders tool: ${parts.join(', ') || (data.status === 'act' ? 'action needed' : 'something to watch')}`;
}

export default function ToolHealthPanel() {
  const [hours, setHours] = useState(48);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const load = useCallback((h) => {
    setLoading(true);
    setError(null);
    return fetch(`/api/tool-health/summary?hours=${h}`)
      .then((r) => r.json())
      .then((d) => {
        if (d && d.error) throw new Error(d.error);
        setData(d);
        // Something needs a human — don't make them click to find out what
        if (d.status === 'act') setExpanded(true);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(hours);
  }, [load, hours]);

  const status = data?.status || (error ? 'watch' : 'healthy');
  const dot = STATUS_DOT[status] || STATUS_DOT.watch;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
      {/* Collapsed line — always visible */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full px-5 py-3 flex items-center justify-between gap-4 text-left"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {loading && !data ? (
            <div className="w-3.5 h-3.5 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin flex-shrink-0" />
          ) : (
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot}`} />
          )}
          <p className={`text-sm font-semibold truncate ${status === 'act' ? 'text-red-800' : status === 'watch' ? 'text-amber-800' : 'text-gray-900'}`}>
            {loading && !data ? 'Checking the orders tool…' : error ? 'Orders tool: check failed' : headline(data)}
          </p>
          {data && !error && (
            <span className="text-xs text-gray-400 hidden sm:inline truncate">
              {data.counts.errors === 0
                ? `no errors logged in the last ${hours === 168 ? '7 days' : `${hours}h`}`
                : `${data.counts.errors} error${data.counts.errors === 1 ? '' : 's'} logged in the last ${hours === 168 ? '7 days' : `${hours}h`}`}
              {' · SFTP '}
              {data.sftp.status === 'ok' ? 'ran' : data.sftp.status}
              {data.renderLatency[0]?.seconds != null && ` · last order's art in ${data.renderLatency[0].seconds}s`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {data && <span className="text-[11px] text-gray-400 hidden md:inline">checked {fmtClock(data.checkedAt)}</span>}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {error && (
            <div className="px-5 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>
          )}

          {data && (
            <>
              {/* Errors by type */}
              <div className="px-5 py-3 flex items-center justify-between gap-4 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Errors in the orders tool log
                </h3>
                <div className="flex items-center gap-1">
                  {WINDOWS.map((w) => (
                    <button
                      key={w.hours}
                      type="button"
                      onClick={() => setHours(w.hours)}
                      className={`px-2 py-0.5 text-[11px] rounded-md border ${
                        hours === w.hours
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {w.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => load(hours)}
                    disabled={loading}
                    className="ml-2 px-2 py-0.5 text-[11px] rounded-md border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {loading ? 'Checking…' : 'Refresh'}
                  </button>
                </div>
              </div>

              {data.groups.length === 0 ? (
                <div className="px-5 py-5 text-sm text-gray-500">
                  No errors logged in the last {hours === 168 ? '7 days' : `${hours} hours`}.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left px-5 py-2.5 text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-400 uppercase tracking-wider">Count</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-400 uppercase tracking-wider">First / Last</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-400 uppercase tracking-wider">Verdict</th>
                        <th className="text-left px-3 py-2.5 pr-5 text-xs font-medium text-gray-400 uppercase tracking-wider">What it means · What to do</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.groups.map((g, idx) => (
                        <tr key={g.type} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} border-b border-gray-100/70 align-top`}>
                          <td className="px-5 py-3">
                            <p className="font-mono text-xs font-medium text-gray-900">{g.type}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{g.label}</p>
                            {!g.known && (
                              <span className="inline-block mt-1 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                new type
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 tabular-nums text-gray-700">
                            {g.count}
                            {g.last1h > 0 && <span className="block text-[11px] text-gray-400">{g.last1h} in last hour</span>}
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                            {fmtTime(g.firstAt)}
                            <br />
                            {fmtTime(g.lastAt)}
                          </td>
                          <td className="px-3 py-3">
                            <VerdictBadge verdict={g.verdict} />
                          </td>
                          <td className="px-3 py-3 pr-5 max-w-xl">
                            <p className="text-xs text-gray-700">{g.explanation}</p>
                            <p className={`text-xs mt-1 font-medium ${g.verdict === 'act' ? 'text-red-700' : g.verdict === 'watch' ? 'text-amber-700' : 'text-emerald-700'}`}>
                              {g.action}
                            </p>
                            {g.orderUids.length > 0 && (
                              <p className="text-[11px] font-mono text-gray-500 mt-1">Orders: {g.orderUids.join(', ')}</p>
                            )}
                            {g.ignoreReason && (
                              <div className="mt-1.5">
                                <button
                                  type="button"
                                  onClick={() => setShowWhy((v) => !v)}
                                  title={`The tool's renderer rejects a serial containing '#'. ${g.ignoreReason.codes
                                    .map((c) => `${c.order_uid} "${c.serial}"`)
                                    .join(', ')} fail every time it re-tries them.`}
                                  className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium"
                                >
                                  {showWhy ? 'Hide' : 'Why can I ignore this?'}
                                </button>
                                {showWhy && (
                                  <div className="mt-1 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2.5 py-2">
                                    <p>
                                      The tool's renderer rejects any serial containing <span className="font-mono">#</span>. These{' '}
                                      {g.ignoreReason.unrenderable} codes have sat PEND since Jun 2026 and the tool re-tries them on every
                                      render pass, logging one <span className="font-mono">create-qr-code</span> error each — so every burst is
                                      a multiple of {g.ignoreReason.unrenderable}. Our 5-minute QR cron has skipped them since Oct 19 2026.
                                    </p>
                                    <ul className="mt-1 font-mono">
                                      {g.ignoreReason.codes.map((c) => (
                                        <li key={`${c.order_uid}-${c.serial}`}>
                                          {c.order_uid} · "{c.serial}"
                                        </li>
                                      ))}
                                    </ul>
                                    <p className="mt-1">
                                      The fix is renaming those serials in the orders tool. A burst that is not a multiple of{' '}
                                      {g.ignoreReason.unrenderable}, or a renderable code stuck PEND, flips this to <strong>act</strong>.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {g.verdict !== 'ignore' && g.sample && (
                              <details className="mt-1.5">
                                <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-gray-600">Last payload</summary>
                                <pre className="mt-1 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 whitespace-pre-wrap break-all">
                                  {g.sample}
                                </pre>
                              </details>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Live signals */}
              <div className="px-5 py-4 grid grid-cols-1 lg:grid-cols-2 gap-5 border-t border-gray-100">
                <div>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Nightly SFTP to Capital One</h3>
                  <div className="flex items-start gap-2">
                    <span
                      className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        data.sftp.status === 'ok' ? 'bg-emerald-500' : data.sftp.status === 'unknown' ? 'bg-gray-400' : 'bg-red-500'
                      }`}
                    />
                    <p className="text-xs text-gray-700">{data.sftp.detail}</p>
                  </div>
                  {data.sftp.recent.length > 0 && (
                    <p className="text-[11px] text-gray-400 mt-1.5 font-mono">
                      {data.sftp.recent
                        .slice(0, 4)
                        .map((r) => `${r.ptDate} ${r.type.replace('sftp-', '')}`)
                        .join(' · ')}
                    </p>
                  )}

                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2 mt-4">Orders tool</h3>
                  {data.tool ? (
                    data.tool.reachable ? (
                      <p className="text-xs text-gray-700">
                        Reachable · reports {data.tool.numPending} pending code{data.tool.numPending === 1 ? '' : 's'}
                        {data.unrenderable.count > 0 && data.tool.numPending === data.unrenderable.count && (
                          <span className="text-gray-400"> (all {data.unrenderable.count} are the unrenderable '#' serials)</span>
                        )}
                        {data.unrenderable.renderableStuck > 0 && (
                          <span className="text-red-700 font-medium"> · {data.unrenderable.renderableStuck} renderable code{data.unrenderable.renderableStuck === 1 ? '' : 's'} stuck</span>
                        )}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-700">Unreachable: {data.tool.error}</p>
                    )
                  ) : (
                    <p className="text-xs text-gray-400">Not checked</p>
                  )}
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    QR art render time · last {data.renderLatency.length} orders
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {data.renderLatency.map((l) => (
                      <span
                        key={l.orderUid}
                        title={`${l.source} · ${l.status} · landed ${fmtTime(l.createdAt)} · ${l.done}/${l.codes} codes done`}
                        className={`inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-md border ${LATENCY_STYLE[l.state] || LATENCY_STYLE.no_codes}`}
                      >
                        <span className="font-medium">{l.orderUid}</span>
                        <span>{latencyLabel(l)}</span>
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">
                    Time from the order landing to its last code rendered. Normal is 12–30s; over 120s is slow; an order
                    still rendering after 10 minutes is stuck.
                  </p>
                </div>
              </div>

              <div className="px-5 py-2.5 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400">
                <span>
                  Verdicts come from a lookup of every error type the tool has logged since 2024. Act items also email
                  david@ and chris@ (at most once per rule every 6 hours).
                </span>
                <span>Checked {fmtTime(data.checkedAt)}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
